import axios from 'axios';
import { debugService, isPrivateCaptureResponse } from './debugService';
import { markSessionExpired, shouldBounceToSignIn } from './sessionExpiry';
import { REACT_APP_API_URL } from '../config/env';
import { installPracticeGuard } from './practice/practiceGuard';
import {
  DENIAL_PROP,
  PERMISSION_DENIED_CODE,
  emitPermissionDenied,
  normalizeDenial,
} from './permissionDenied';

export const API_BASE_URL = REACT_APP_API_URL;

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// TEST MEETINGS (services/practice): while one is open, this client's meeting
// writes are answered in the tab and never sent. A no-op the rest of the time.
installPracticeGuard(api);

// Bounded, window-aware self-healing retry for HTTP 429 on idempotent GETs
// (see the response interceptor below). This is the single retry authority for
// rate-limited reads — callers must not layer their own 429 retry on top. The
// backend limiter's Retry-After is the remaining window (up to 60s); honoring
// it up to MAX_429_BACKOFF_MS across a few retries lets a read wait out a full
// limiter window and recover, while capping any single wait so a foreground
// load never freezes for more than 30s.
const MAX_429_RETRIES = 3;
const MAX_429_BACKOFF_MS = 30_000;

// c3 — snapshot diff summary on PUT /draft requests. Keyed by URL so multiple
// canvases in the same session each compare against their own prior PUT. The
// summary is attached to the api_request log meta as `snapshot_changed_since_last`
// (bool) and `snapshot_diff_summary` (small object) so a reader can grep the
// dev-mode export for "what actually changed between consecutive autosaves"
// without diffing full bodies.
const lastDraftSnapshotByUrl: Map<string, any> = new Map();
const DRAFT_URL_RE = /\/workflows\/[^/]+\/[^/]+\/draft$/;

function summarizeSnapshotDiff(prev: any, next: any) {
  const prevNodes: any[] = prev?.canvas_nodes ?? [];
  const nextNodes: any[] = next?.canvas_nodes ?? [];
  const prevEdges: any[] = prev?.canvas_edges ?? [];
  const nextEdges: any[] = next?.canvas_edges ?? [];

  const prevNodeIds = new Set(prevNodes.map(n => n?.node_id));
  const nextNodeIds = new Set(nextNodes.map(n => n?.node_id));
  const prevEdgeIds = new Set(prevEdges.map(e => e?.edge_id));
  const nextEdgeIds = new Set(nextEdges.map(e => e?.edge_id));

  const nodesAdded = nextNodes.filter(n => !prevNodeIds.has(n?.node_id)).map(n => n?.node_id);
  const nodesRemoved = prevNodes.filter(n => !nextNodeIds.has(n?.node_id)).map(n => n?.node_id);
  const edgesAdded = nextEdges.filter(e => !prevEdgeIds.has(e?.edge_id)).map(e => e?.edge_id);
  const edgesRemoved = prevEdges.filter(e => !nextEdgeIds.has(e?.edge_id)).map(e => e?.edge_id);

  // Field-level diff for nodes that exist in both. Lists which node IDs had
  // a change to name/position/config_json/etc. Bounded to avoid blowing up
  // the log on a huge canvas.
  const nodesModified: Array<{ node_id: string; changed: string[] }> = [];
  for (const next_n of nextNodes) {
    const prev_n = prevNodes.find(n => n?.node_id === next_n?.node_id);
    if (!prev_n) continue;
    const changed: string[] = [];
    for (const k of ['name', 'description', 'instructions', 'position_x', 'position_y', 'config_json', 'task_type', 'stage_key', 'stage_group']) {
      if (prev_n[k] !== next_n[k]) changed.push(k);
    }
    if (changed.length > 0) nodesModified.push({ node_id: next_n.node_id, changed });
    if (nodesModified.length >= 20) break;
  }

  const edgesModified: Array<{ edge_id: string; changed: string[] }> = [];
  for (const next_e of nextEdges) {
    const prev_e = prevEdges.find(e => e?.edge_id === next_e?.edge_id);
    if (!prev_e) continue;
    const changed: string[] = [];
    for (const k of ['source_node_id', 'target_node_id', 'source_handle', 'target_handle', 'label', 'condition_json', 'delay_json', 'fire_mode']) {
      if (prev_e[k] !== next_e[k]) changed.push(k);
    }
    if (changed.length > 0) edgesModified.push({ edge_id: next_e.edge_id, changed });
    if (edgesModified.length >= 20) break;
  }

  const settingsChanged = JSON.stringify(prev?.settings ?? {}) !== JSON.stringify(next?.settings ?? {});

  const totalChanges =
    nodesAdded.length + nodesRemoved.length + nodesModified.length
    + edgesAdded.length + edgesRemoved.length + edgesModified.length
    + (settingsChanged ? 1 : 0);

  return {
    total_changes: totalChanges,
    nodes_added: nodesAdded,
    nodes_removed: nodesRemoved,
    nodes_modified: nodesModified,
    edges_added: edgesAdded,
    edges_removed: edgesRemoved,
    edges_modified: edgesModified,
    settings_changed: settingsChanged,
  };
}

api.interceptors.request.use((config) => {
  if (debugService.isEnabled) {
    (config as any).__debugStartMs = Date.now();

    // c3 — compute snapshot diff for /draft autosaves so the dev-mode reader
    // can answer "what changed?" at a glance. Only fires on PUT to the
    // /draft route; everything else logs unchanged.
    let meta: Record<string, any> | undefined;
    const method = (config.method || 'GET').toUpperCase();
    const url = config.url || '';
    if (method === 'PUT' && DRAFT_URL_RE.test(url)) {
      const snap = (config.data as any)?.snapshot;
      const prev = lastDraftSnapshotByUrl.get(url);
      if (snap) {
        if (prev) {
          const diff = summarizeSnapshotDiff(prev, snap);
          meta = {
            snapshot_changed_since_last: diff.total_changes > 0,
            snapshot_diff_summary: diff,
          };
        } else {
          meta = { snapshot_changed_since_last: null, snapshot_diff_summary: 'first save in session' };
        }
        lastDraftSnapshotByUrl.set(url, snap);
      }
    }

    // The request event's id rides on the config so the answer can take the
    // body back out if the server marks it private (SEC-W4-054).
    (config as any).__debugRequestEventId = debugService.logApiRequest(method, url, config.data, meta);
  }
  return config;
});

api.interceptors.response.use(
  (response) => {
    if (debugService.isEnabled) {
      const start = (response.config as any).__debugStartMs;
      const duration = start ? Date.now() - start : 0;
      debugService.logApiResponse(
        response.config.method || 'GET',
        response.config.url || '',
        response.status,
        response.data,
        duration,
        { private: isPrivateCaptureResponse(response.headers), requestEventId: (response.config as any).__debugRequestEventId },
      );
    }
    return response;
  },
  async (error) => {
    // Self-heal transient rate-limits on idempotent reads. The unified inbox
    // fans out many GETs per user action and can trip the backend's per-user
    // limiter (routes/inbox.ts). A bounded, backing-off retry keeps thread
    // lists / conversation / status reads loading instead of surfacing a hard
    // error — which the inbox otherwise misreads as a disconnect. GET-only, so
    // a mutation is never silently replayed; we honor the server's Retry-After.
    const cfg: any = error?.config;
    const httpStatus = error?.response?.status;
    const method = (cfg?.method || 'get').toLowerCase();
    if (httpStatus === 429 && cfg && method === 'get') {
      const attempt = (cfg.__retry429 || 0) + 1;
      if (attempt <= MAX_429_RETRIES) {
        cfg.__retry429 = attempt;
        const retryAfter = Number(error?.response?.headers?.['retry-after']);
        const backoffMs = Number.isFinite(retryAfter) && retryAfter > 0
          ? Math.min(retryAfter * 1000 + 250, MAX_429_BACKOFF_MS)
          : Math.min(500 * 2 ** (attempt - 1), MAX_429_BACKOFF_MS);
        // Keep the retried 429 visible in dev-mode captures. debugService only
        // sees traffic through these interceptors, so a silently-retried 429
        // would otherwise vanish from the Debug Session export that diagnoses
        // exactly this limiter pressure.
        if (debugService.isEnabled) {
          const start = (cfg as any).__debugStartMs;
          debugService.logApiResponse(method, cfg.url || '', httpStatus, error?.response?.data, start ? Date.now() - start : 0,
            { private: isPrivateCaptureResponse(error?.response?.headers), requestEventId: (cfg as any).__debugRequestEventId });
        }
        await new Promise(resolve => setTimeout(resolve, backoffMs + Math.floor(Math.random() * 200)));
        return api(cfg);
      }
    }
    // ── The one permission-refusal branch in the app ───────────────────
    // Before this, a single server condition surfaced five different ways:
    // an honest toast, a hardcoded generic string, the raw axios
    // "Request failed with status code 403", an EMPTY SURFACE (loaders that
    // `catch { setRows([]) }`, so a blocked user reads "no results" with every
    // button live), and an optimistic edit that silently snapped back. Seven
    // detail screens reported it as data loss — "Client not found" for a client
    // that exists fine.
    //
    // This interceptor runs BEFORE every one of those catch blocks, so
    // normalising here makes all of them honest at once without touching them.
    // The denial is stamped on the error (so any `catch` can render
    // <PermissionDenied> instead of guessing) and announced once (so exactly
    // one toast appears, naming the switch and where to grant it).
    if (httpStatus === 403 && error?.response?.data?.code === PERMISSION_DENIED_CODE) {
      const denial = normalizeDenial(error.response.data, method, cfg?.url);
      try {
        (error as any)[DENIAL_PROP] = denial;
      } catch {
        // A frozen error object is not worth failing the request over; the
        // event below still reaches the toast.
      }
      emitPermissionDenied(denial);
    }

    // ── Session expiry: say so ONCE, instead of failing silently forever ──
    // Diagnosed from a production export: a 3h52m session expired and nothing
    // noticed. Every 60-second poller kept firing into it for eight minutes —
    // 100 requests, all 401, 8 of them unhandled rejections — while the page
    // looked healthy and quietly stopped updating.
    //
    // Every 401 the frontend can actually reach means "no session" (the
    // backend's three wordings — Authentication required / Not authenticated /
    // Unauthorized — are all the same condition; the only other 401s are
    // webhook signature checks the browser never calls). The `/auth/` guard is
    // what stops the sign-in page bouncing off itself: 401 there is the NORMAL
    // reply to "is anyone signed in?", not an expiry.
    if (shouldBounceToSignIn(httpStatus, cfg?.url)) {
      markSessionExpired();
      if (typeof window !== 'undefined') {
        // Reload rather than client-navigate: it tears down every interval and
        // in-flight request in one go and re-runs the real auth check.
        window.location.assign('/');
      }
    }

    if (debugService.isEnabled) {
      debugService.logApiError(
        error?.config?.method || 'GET',
        error?.config?.url || '',
        error,
        { requestEventId: (error?.config as any)?.__debugRequestEventId },
      );
    }
    return Promise.reject(error);
  },
);
